import { DownloadIcon, SearchIcon, SortAscIcon, SortDescIcon } from '@primer/octicons-react';
import { ActionList, ActionMenu, Box, Button, IconButton } from '@primer/react';
import React, { useContext, useMemo } from 'react';
import Text from '../text';
import TextInput from '../text-input';
import { DataTableContainerContext, eSortMode, IDataTableContext } from './DataTable';
import * as XLSX from "xlsx";

const Toolbar = () => {
    const { props, setSearchKey, sortConfig, setSortConfig, sortByField } = useContext<IDataTableContext>(DataTableContainerContext);
    const { title, titleComponent, subTitle, subTitleComponent, actionComponent, searchEnable, columns, exportEnable } = props;

    const getCellValue = (col: any, row: any): string | number | boolean => {
        if (col.cellRender) {
            const rendered = col.cellRender(row);
            if (typeof rendered === "string" || typeof rendered === "number" || typeof rendered === "boolean") {
                return rendered;
            }
            if (React.isValidElement(rendered)) {
                const element = rendered as React.ReactElement;
                if (element.props.children) {
                    if (typeof element.props.children === "string") {
                        return element.props.children;
                    }
                    if (Array.isArray(element.props.children)) {
                        return element.props.children
                            .map((child: any) => (typeof child === "string" ? child : ""))
                            .join(" ");
                    }
                }
            }
        }

        return row[col.dataField] ?? "";
    };

    const handleExportToExcel = () => {
        if (!props.exportEnable) return;
        const visibleColumns = columns.filter(
            (col) => col.caption !== "selection" && col.caption !== "#"
        );
        const headers = visibleColumns.map((col) => col.caption || col.dataField);
        const exportData = props.data.map((row) =>
            visibleColumns.map((col) => getCellValue(col, row))
        );
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...exportData]);
        const columnWidths = headers.map((header, i) => ({
            wch: Math.max(
                props.columns.length,
                ...exportData.map((row) => String(row[i] ?? "").length)
            ) + 2,
        }));
        worksheet["!cols"] = columnWidths;
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, "DataTableExport.xlsx");
    };

    const sortByName = useMemo(() => {
        return props.columns
            .filter((x) => x.dataField === sortByField)
            .map((x) => x.caption)
            .join(",");
    }, [columns, sortByField]);
    return (
        <Box id="toolbar" sx={{ display: "flex", mb: 1 }}>
            <Box
                id="left"
                sx={{
                    flex: 1,
                }}
            >
                {title && (
                    <Text
                        text={title}
                        sx={{
                            fontWeight: 600,
                            fontSize: 22,
                        }}
                    />
                )}
                {titleComponent && <Box>{titleComponent}</Box>}
                {(subTitle || subTitleComponent) &&
                    <Box>
                        {subTitle && <Text text={subTitle} sx={{
                            color: "fg.muted",
                            fontSize: "0.9rem"
                        }} />
                        }
                        {subTitleComponent && <>{subTitleComponent}</>}
                    </Box>
                }

            </Box>
            <Box
                id="actions"
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                }}
            >
                {actionComponent}
                {exportEnable && (
                    <Box sx={{ ml: 2 }}>

                        <Button variant="default" size='small' leadingVisual={DownloadIcon} onClick={handleExportToExcel}>Export</Button>

                    </Box>
                )}
                {searchEnable && (
                    <Box sx={{ ml: 2 }}>
                        <TextInput
                            leadingVisual={SearchIcon}
                            placeholder={"Search"}
                            onValueDelayChanged={(value) => {
                                setSearchKey?.(value)
                            }}
                        ></TextInput>
                    </Box>
                )}
                {sortConfig && sortConfig.enable && (
                    <Box sx={{ ml: 2 }}>
                        <ActionMenu>
                            <ActionMenu.Button
                                leadingVisual={
                                    sortConfig?.mode === eSortMode.DESC
                                        ? SortDescIcon
                                        : SortAscIcon
                                }
                            >
                                Sắp xếp theo <b>{sortByName}</b>
                            </ActionMenu.Button>
                            <ActionMenu.Overlay>
                                <ActionList
                                    selectionVariant="single"
                                    role="menu"
                                    aria-label=""
                                >
                                    {props.columns
                                        .filter((x) => x.dataField)
                                        .map((x, idx) => {
                                            return (
                                                <ActionList.Item
                                                    key={idx}
                                                    role="menuitemcheckbox"
                                                    selected={x.dataField === sortConfig.field}
                                                    onSelect={() => {
                                                        if (sortConfig && setSortConfig)
                                                            setSortConfig({
                                                                ...sortConfig,
                                                                field: x.dataField ?? "",
                                                            });
                                                    }}
                                                >
                                                    {x.caption}
                                                </ActionList.Item>
                                            );
                                        })}

                                    <ActionList.Divider></ActionList.Divider>
                                    <ActionList.Item
                                        role="menuitemcheckbox"
                                        selected={sortConfig.mode === eSortMode.ASC}
                                        onSelect={() => {
                                            if (sortConfig && setSortConfig)
                                                setSortConfig({
                                                    ...sortConfig,
                                                    mode: eSortMode.ASC,
                                                });
                                        }}
                                    >
                                        <ActionList.LeadingVisual>
                                            <SortAscIcon />
                                        </ActionList.LeadingVisual>
                                        Ascending
                                    </ActionList.Item>
                                    <ActionList.Item
                                        role="menuitemcheckbox"
                                        selected={sortConfig.mode === eSortMode.DESC}
                                        onSelect={() => {
                                            if (sortConfig && setSortConfig)
                                                setSortConfig({
                                                    ...sortConfig,
                                                    mode: eSortMode.DESC,
                                                });
                                        }}
                                    >
                                        <ActionList.LeadingVisual>
                                            <SortDescIcon />
                                        </ActionList.LeadingVisual>
                                        Descending
                                    </ActionList.Item>
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Box>
                )}
                {/* {exportEnable && (
                    <Box sx={{ ml: 2 }}>
                        <IconButton
                            icon={DownloadIcon}
                            variant="default"
                            aria-label="Download"
                        />
                    </Box>
                )} */}
            </Box>
        </Box>
    );
};

export default Toolbar;